import React from 'react';
import { Card, ContentCard, Filter, FilterSetController, Table, TooltipHelp, Icon, Numbers, Tooltip } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../context/useAppContext';
import Style from './crossSellingStats.module.css';
import i18n from 'i18next';
import LoadingTable from '../../components/loadingTable';
import { textIncludes } from './utils';

export const ProductVariantRow = React.memo(({ data = null, locale, currency, category }) => {
    return (
        <Table.Tr>
            <td>{data.name}</td>
            <td>{category}</td>
            <td>
                <Numbers locale={locale} value={data.price} currency={currency} showNullDecimal={Boolean(currency)} />
            </td>
            <td>{data.count_sales}</td>
            <td>
                <Numbers locale={locale} value={data.sum_sales_tax_included} currency={currency} showNullDecimal={Boolean(currency)} />
            </td>
            <td>
                <Numbers locale={locale} value={data.sum_sales} currency={currency} showNullDecimal={Boolean(currency)} />
            </td>
            <td></td>
        </Table.Tr>
    );
});

export const ProductRow = React.memo(({ data = null, locale = 'en-GB', currency = null }) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = React.useState(false);

    const handleClick = () => {
        setIsExpanded(currentState => !currentState);
    };
    const productCategories = {
        1: {
            label: t('ticket.cross-selling.cashless'),
            icon: 'credit-card'
        },
        2: {
            label: t('ticket.cross-selling.product'),
            icon: 'shirt'
        },
        3: {
            label: t('ticket.cross-selling.service'),
            icon: 'service'
        }
    };

    return (
        <>
            <Table.Tr className={Style['product-row']} onClick={handleClick}>
                <td colSpan="3">
                    <div className={Style['product-title']}>
                        <span>
                            <Icon name={productCategories[data.category_id].icon} />
                            {data.name}
                        </span>
                        {data.for_buyer ? (
                            <Tooltip className={Style['product-tooltip']} triggerOnHover title={t('ticket.cross-selling.for_buyers')}>
                                <Icon color="ticket" name="user" />
                            </Tooltip>
                        ) : (
                            <Tooltip className={Style['product-tooltip']} triggerOnHover title={t('ticket.cross-selling.for_attendees')}>
                                <Icon color="ticket" name="tickets" />
                            </Tooltip>
                        )}
                    </div>
                </td>
                <td>{data.count_sales}</td>
                <td>
                    <Numbers locale={locale} value={data.sum_sales_tax_included} currency={currency} showNullDecimal={Boolean(currency)} />
                </td>
                <td>
                    <Numbers locale={locale} value={data.sum_sales} currency={currency} showNullDecimal={Boolean(currency)} />
                </td>
                <td>{data.variants?.length > 0 && <Icon name={'chevron-bottom'} className={isExpanded ? Style['rotate-180'] : ''} onClick={handleClick} />}</td>
            </Table.Tr>
            {isExpanded &&
                Object.entries(data.variants).map(([index, value]) => (
                    <ProductVariantRow key={`variant-${index}`} data={value} locale={locale} currency={currency} category={productCategories[data.category_id].label} />
                ))}
        </>
    );
});

export const ProductStats = React.memo(({ stats = null, locale, currency }) => {
    return (
        <>
            {Object.entries(stats)
                .sort(([, a], [, b]) => {
                    return b.count_sales - a.count_sales;
                })
                .map(([index, value]) => (
                    <ProductRow key={`product-${index}`} data={value} locale={locale} currency={currency} />
                ))}
        </>
    );
});

export default function CrossSellingStats({ stats = null }) {
    const locale = i18n.language;
    const { t } = useTranslation();
    const [{ event }] = useAppContext();
    const [search, setSearch] = React.useState('');
    const [filters, setFilters] = React.useState({});

    const filterOptions = {
        for_buyer: [
            {
                text: t('ticket.cross-selling.for_buyers'),
                value: true
            },
            {
                text: t('ticket.cross-selling.for_attendees'),
                value: false
            }
        ],
        category_id: [
            {
                text: t('ticket.cross-selling.cashless'),
                value: 1
            },
            {
                text: t('ticket.cross-selling.product'),
                value: 2
            },
            {
                text: t('ticket.cross-selling.service'),
                value: 3
            }
        ]
    };

    const _stats = React.useMemo(() => {
        if (!stats) {
            return [];
        }
        let statsFound = stats.find(statsGroup => statsGroup.name === 'sales_by_cross_sellings_stats').value;
        return statsFound;
    }, [stats]);

    const filteredStats = React.useMemo(() => {
        if (Object.keys(filters).length === 0 && !search) {
            return _stats;
        }

        return _stats.reduce((products, product) => {
            const currentProduct = { ...product };
            const areFiltersOk = Object.keys(filterOptions).every(key => {
                return key in filters ? filters[key].has(product[key]) : true;
            });
            if (!areFiltersOk) {
                return products;
            }
            if (search) {
                currentProduct.variants = currentProduct.variants.filter(variant => {
                    return textIncludes(variant.name, search);
                });
                if (currentProduct.variants.length > 0 || textIncludes(product.name, search)) {
                    products.push(currentProduct);
                }
            } else {
                products.push(currentProduct);
            }
            return products;
        }, []);
    }, [_stats, search, filters]);

    const productCount = filteredStats?.length || 0;

    return (
        <Card>
            <ContentCard>
                <FilterSetController
                    total={productCount}
                    isLoadingTotal={!filteredStats}
                    labelTotal={t('ticket.cross-selling.product', { count: productCount })}
                    labelReset={t('ticket.common.action.reset')}
                    search={{
                        onChange: (synth, value) => {
                            setSearch(value);
                        },
                        handleReset: () => {
                            setSearch('');
                        },
                        displayResetIcon: true,
                        placeholder: t('ticket.common.action.search'),
                        value: search
                    }}
                    onChange={values => {
                        setFilters(values);
                    }}
                >
                    <Filter
                        name="category_id"
                        labelName={t('ticket.cross-selling.category')}
                        hierarchy
                        options={filterOptions['category_id']}
                        index="1"
                        labelSelectAll={t('ticket.common.action.select-all')}
                        labelCTA={t('ticket.common.action.apply')}
                    />
                    <Filter
                        name="for_buyer"
                        labelName={t('ticket.cross-selling.target')}
                        hierarchy
                        options={filterOptions['for_buyer']}
                        index="2"
                        labelSelectAll={t('ticket.common.action.select-all')}
                        labelCTA={t('ticket.common.action.apply')}
                    />
                </FilterSetController>
            </ContentCard>
            <Table useNew className={Style['cross-selling-table']}>
                <Table.THead>
                    <Table.Tr isHeader>
                        <th>{t('ticket.cross-selling.product')}</th>
                        <th>{t('ticket.cross-selling.category')}</th>
                        <th>{t('ticket.common.price')}</th>
                        <th>{t('ticket.common.quantity')}</th>
                        <th>
                            {t('ticket.common.revenue_tax')}
                            <TooltipHelp triggerOnHover title={t('ticket.common.revenue_tax_tooltip')} />
                        </th>
                        <th>
                            {t('ticket.common.revenue_no_tax')}
                            <TooltipHelp triggerOnHover title={t('ticket.common.revenue_no_tax_tooltip')} />
                        </th>
                        <th></th>
                    </Table.Tr>
                </Table.THead>
                {filteredStats?.length > 0 && (
                    <Table.TBody>
                        <ProductStats stats={filteredStats} locale={locale} currency={event.currency || 'EUR'}></ProductStats>
                    </Table.TBody>
                )}
            </Table>
            <LoadingTable loading={!stats} />
            {!filteredStats?.length && <Card.EmptyState title={t('ticket.cross-selling.no_product_found')} icon={<Icon name="credit-card" />} />}
        </Card>
    );
}
